import { VariantProps, cva } from 'class-variance-authority';
import { ChangeEvent, forwardRef, useState } from 'react';
import * as React from 'react';

import { Typography } from '../typography/typography';
import { Loading } from '../icons';
import { cn } from '../../utils';

export interface DropzoneProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof dropzoneVariants> {
  onFileSelect?: (file: File | null) => void;
  validateFile?: (file: File) => boolean;

  uploadLabel: string;
  uploadDescription: string;
  loadingLabel?: string;
  loadingDescription?: string;
  errorLabel?: string;
  errorMessage?: string | false;
  invalidFileFormatMessage?: string;
  fileTooLargeMessage?: string;
  multipleFilesUploadedErrorMessage?: string;

  acceptedFormats?: string[];
  maxSizeMB?: number;
  hasError?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  isLoading?: boolean;
}

export const dropzoneVariants = cva(
  `relative flex flex-col items-center justify-center w-full p-8 border-2 border-dashed border-surface
   rounded-lg cursor-pointer transition-colors duration-200 ease-in-out text-center bg-surface-weak
   text-surface hover:bg-surface-weak/50
   `,
  {
    variants: {
      size: {
        small: 'p-4 min-h-20',
        medium: 'p-6 min-h-32',
        large: 'p-8 min-h-40',
      },
      hasError: {
        true: 'border-critical',
      },
      isDragging: {
        true: 'bg-surface-weak/50',
      },
      disabled: {
        true: 'border-border-disabled bg-surface-disabled cursor-not-allowed',
      },
    },
    defaultVariants: {
      size: 'medium',
      hasError: false,
      isDragging: false,
      disabled: false,
    },
  },
);

export type DropzoneComponentProps = React.ComponentPropsWithoutRef<'input'> & DropzoneProps;

export const Dropzone = forwardRef<HTMLInputElement, DropzoneProps>(
  (
    {
      multipleFilesUploadedErrorMessage,
      invalidFileFormatMessage,
      fileTooLargeMessage,
      loadingDescription,
      acceptedFormats = [],
      isLoading = false,
      uploadDescription,
      maxSizeMB = 10,
      validateFile,
      loadingLabel,
      errorMessage,
      onFileSelect,
      uploadLabel,
      errorLabel,
      className,
      hasError,
      disabled,
      multiple,
      size,
      ...props
    },
    ref,
  ) => {
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const validateFileDefault = (file: File): boolean => {
      if (file.size > maxSizeMB * 1024 * 1024) {
        setError(fileTooLargeMessage || `File is too large. Maximum size is ${maxSizeMB}MB.`);
        return false;
      }

      if (acceptedFormats.length > 0) {
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';
        if (
          !acceptedFormats.includes(`.${fileExtension}`) &&
          !acceptedFormats.includes(fileExtension)
        ) {
          setError(
            invalidFileFormatMessage ||
              `Invalid file format. Accepted formats: ${acceptedFormats.join(', ')}`,
          );
          return false;
        }
      }

      setError(null);
      return true;
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (!multiple && e.dataTransfer.files.length > 1) {
        setError(multipleFilesUploadedErrorMessage || `You cannot upload more than 1 file`);
        return;
      }

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        if (file && (validateFile || validateFileDefault)(file)) {
          setSelectedFile(file);
          onFileSelect?.(file);
        } else {
          setSelectedFile(null);
          onFileSelect?.(null);
        }
      }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      if (file && (validateFile || validateFileDefault)(file)) {
        setSelectedFile(file);
        onFileSelect?.(file);
      } else {
        setSelectedFile(null);
        onFileSelect?.(null);
      }
    };

    return (
      <div className="w-full">
        <div
          className={cn(
            dropzoneVariants({
              hasError: hasError || !!error,
              isDragging,
              disabled,
              size,
            }),
            className,
          )}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            className={cn('absolute inset-0 w-full h-full opacity-0 cursor-pointer', {
              'cursor-not-allowed': disabled || isLoading,
            })}
            disabled={disabled || isLoading}
            onChange={handleFileChange}
            multiple={multiple}
            type="file"
            ref={ref}
            {...props}
          />

          {isLoading ? (
            <div className="flex flex-col items-center justify-center">
              <Typography className="mb-1" variant="h200">
                {loadingLabel}
              </Typography>

              <Typography variant="paragraphBasicBook" className="mb-2">
                {loadingDescription}
              </Typography>

              <Loading className="size-8 animate-spin text-surface" />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <Typography className="mb-1" variant="h200">
                {uploadLabel}
              </Typography>

              <Typography variant="paragraphBasicBook">{uploadDescription}</Typography>

              {selectedFile && (
                <div className="mt-4 px-4 py-2 bg-surface-weak rounded-md">
                  <Typography variant="labelMediumMedium">{selectedFile.name}</Typography>
                </div>
              )}
            </div>
          )}
        </div>

        {(error || errorMessage) && (
          <>
            <Typography className="mb-1 mt-3 text-critical" variant="h200">
              {errorLabel}
            </Typography>

            <Typography variant="paragraphBasicBook" className="text-critical">
              {error || errorMessage}
            </Typography>
          </>
        )}
      </div>
    );
  },
);

Dropzone.displayName = 'Dropzone';
